import type { UntypedFunction } from '@mnd-frontend/ts';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ACCEPT_VIDEO_FILES, FILE_ERROR_MESSAGE_TIMEOUT, type UploadingStates } from '../constants';
import ErrorMessageInternal from '../ErrorMessage';
import FileInputField from '../FileInputField';
import UploadWrapper from '../UploadWrapper';
import { createFileValidators } from '../validators/file';
import VideoUploadPreview from './VideoUploadPreview';

const Wrapper = styled(UploadWrapper)`
  display: flex;
  flex-direction: column;
`;

interface VideoUploaderProps {
  imageUrl?: string;
  caption?: string;
  loading?: boolean;
  isDraft?: boolean;
  disabled?: boolean;
  processing?: boolean;
  action: UntypedFunction;
  deleteCallback?: () => void;
  validatorProps?: { validFileFormats: typeof ACCEPT_VIDEO_FILES };
}

const VideoUploader = ({
  imageUrl,
  caption,
  loading,
  isDraft,
  disabled = false,
  processing,
  action,
  deleteCallback,
  validatorProps = { validFileFormats: ACCEPT_VIDEO_FILES },
}: VideoUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<UploadingStates>('IDLE');
  const [t] = useTranslation();

  const [error, setError] = useState('');

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    setError('');
    setStatus('LOADING');
    try {
      setStatus('IDLE');
      if (!!fileValidators && fileValidators.length > 0) {
        const promises = fileValidators.map(validator => validator(file));
        await Promise.all(promises);
      }
      await action(file);
    } catch (e) {
      handleValidationError(e);
      setStatus('IDLE');
    }
  };

  const fileValidators = createFileValidators({
    validFileFormats: validatorProps.validFileFormats,
  });

  const handleValidationError = (error: any) => {
    if (error.code === 'error.fileformat') setError(t('components_video-upload_error_format'));
  };

  useEffect(() => {
    if (!error) return;

    const timeout = setTimeout(() => {
      if (timeout) {
        setError('');
      }
    }, FILE_ERROR_MESSAGE_TIMEOUT);
    return () => {
      clearTimeout(timeout);
    };
  }, [error]);

  return (
    <Wrapper>
      {error ? (
        <ErrorMessageInternal caption={error} />
      ) : (
        <>
          {imageUrl && (
            <VideoUploadPreview
              imageUrl={imageUrl}
              disabled={disabled}
              isDraft={isDraft}
              onChange={() => inputRef.current?.click()}
              handleFileDelete={deleteCallback}
            />
          )}
          <FileInputField
            inputRef={inputRef}
            status={status}
            caption={caption || t('components_video-upload_select-file')}
            loading={processing || loading}
            loadingCaption={
              processing
                ? t('components_video-upload_processing')
                : t('components_video-upload_uploading')
            }
            acceptFormats={validatorProps.validFileFormats}
            onChange={handleFileSelect}
            disabled={disabled}
          />
        </>
      )}
    </Wrapper>
  );
};

export default VideoUploader;
